import { useCallback } from "react";

import { OnboardingAdminError } from "../../OnboardingAPI";
import { useModalContext } from "../../components/ModalProvider";

/** What the cockpit says when there is no sentence of the server to say. */
export const FALLBACK_ERROR_TEXT = "Das hat nicht geklappt. Bitte versuche es noch einmal.";

/** "OnboardingAdminError: ..." and "Error: ..." are class names, not text for a person. */
const withoutClassName = (text: string) => text.replace(/^\s*(?:[A-Za-z]+Error|Error):\s*/, "").trim();

/** A message that is only a status line ("500 Internal Server Error") or a bare code ("invalid_state") is no sentence either. */
const isSentence = (text: string) => !!text && !/^\d{3}\b/.test(text) && !/^[a-z]+(?:_[a-z]+)+$/.test(text);

/**
 * The one place that turns an error of the cockpit into text: the server's own German sentence (`message`), else the
 * German text of its error code, else `fallback`. Never `String(error)`, never a class name in front.
 */
export function errorText(error: unknown, fallback: string = FALLBACK_ERROR_TEXT): string {
  if (error instanceof OnboardingAdminError) {
    for (const candidate of [error.detail, error.message]) {
      const text = candidate ? withoutClassName(candidate) : "";
      if (isSentence(text)) return text;
    }
    return fallback;
  }
  const known = error as { detail?: unknown; message?: unknown } | null;
  for (const candidate of [known?.detail, known?.message]) {
    const text = typeof candidate === "string" ? withoutClassName(candidate) : "";
    // a network failure ("Failed to fetch") is English technical text: not for the screen
    if (isSentence(text) && /[äöüß]|\s/.test(text) && !/^(Failed|Network|Load failed|Unexpected|Cannot|Not found)/i.test(text)) return text;
  }
  return fallback;
}

/**
 * The cockpit's way to show an error: the sentence as a message (the global `pushError` writes `error.toString()`, with
 * the class name in front). A sign-out (401) and everything that is not an answer of the cockpit's routes still go the
 * global way, which knows what to do with them.
 */
export function useCockpitErrors() {
  const { pushError, pushMessage } = useModalContext() as { pushError: (error: unknown) => void; pushMessage: (text: string) => void };
  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof OnboardingAdminError) {
        console.error(error);
        pushMessage(errorText(error));
        return;
      }
      pushError(error);
    },
    [pushError, pushMessage],
  );
  return { showError, pushError };
}
